<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <h1 class="text-left">Invoices</h1>
                <div class="d-flex gap-2">
                    <v-btn v-if="user.super" :loading="recalculating" color="grey-darken-1" variant="outlined"
                           @click="recalculateSubscriptions">
                        Recalculate Subscriptions
                    </v-btn>
                    <v-btn v-if="user.type === 'admin'" color="black" @click="dialogSwitch = true">
                        Generate Invoices
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="3" sm="4">
                <company-select v-model="listFormData['company']" :items="companies" clearable density="compact" label="Select Company"
                                @update:model-value="()=>{currentPage=1;fetchInvoices()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="listFormData['year']" :items="years" clearable density="compact" label="Year"
                          @update:model-value="()=>{currentPage=1;fetchInvoices()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="listFormData['month']" :items="months" clearable density="compact" label="Month"
                          @update:model-value="()=>{currentPage=1;fetchInvoices()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="4">
                <v-select v-model="listFormData['status']" :items="statuses" clearable density="compact"
                          label="Status" @update:model-value="()=>{currentPage=1;fetchInvoices()}"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-data-table v-model:expanded="expanded" :headers="tableHeaders" :items="tableItems"
                              :loading="loading"
                              density="compact"
                              hide-default-footer
                              show-expand
                >
                    <template v-slot:loading>
                        <v-skeleton-loader type="table-row@7"></v-skeleton-loader>
                    </template>
                    <template v-slot:[`item.id`]="{ item }">
                        <td>{{ `NEXTERA-${item.id}` }}</td>
                    </template>
                    <template v-slot:[`item.client_source`]="{ item }">
                        <v-chip v-if="item.client_source" size="x-small" variant="tonal"
                                :color="item.client_source === 'web' ? 'blue' : item.client_source === 'app' ? 'green' : 'grey'">
                            {{ item.client_source }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <td>{{ status(item.status) }}</td>
                    </template>
                    <template v-slot:[`item.action`]="{ item }">
                        <td>
                            <NoteIcon v-if="user.super" entity-type="invoices" :entity-id="item.id" :note-count="item.notes_count ?? 0" class="me-1"/>
                            <v-btn :loading="downloading" class="text-info me-2" size="small"
                                   variant="outlined"
                                   @click="downloadInvoice(item.id)">Download
                            </v-btn>
                            <v-btn class="text-default me-2" size="small" variant="outlined"
                                   @click="showEmailDialog(item)">Email
                            </v-btn>
                            <v-btn v-if="user.type !== 'admin' && !item.status"
                                   :loading="paying" class="text-success" size="small"
                                   variant="outlined" @click="payInvoice(item.id)">Pay
                            </v-btn>
                            <v-btn v-if="user.type === 'admin' && !item.status"
                                   :loading="markingPaid === item.id" class="text-success" size="small"
                                   variant="outlined" @click="markInvoicePaid(item.id)">Mark Paid
                            </v-btn>
                        </td>
                    </template>
                    <template v-slot:expanded-row="{ columns, item }">
                        <tr>
                            <td :colspan="columns.length">
                                <table class="w-100">
                                    <tbody>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Qty</th>
                                        <th>Unit Price</th>
                                        <th>Discount Price</th>
                                        <th>Sub Total</th>
                                    </tr>
                                    <tr v-for="(itemDetail,index) in item.items">
                                        <td
                                            class="cursor-pointer text-decoration-underline"
                                            @click="showInvoiceItemDetail(item, index)">
                                            {{ itemDetail.plan_name }}
                                        </td>
                                        <td>{{ itemDetail.quantity }}</td>
                                        <td>{{ `${itemDetail.unit_price} ${item.currency}` }}</td>
                                        <td>{{
                                                itemDetail.discount_price ? `${itemDetail.discount_price} ${item.currency}` : '-'
                                            }}
                                        </td>
                                        <td>{{
                                                `${parseInt(itemDetail.quantity) * parseFloat(itemDetail.discount_price ?? itemDetail.unit_price)} ${item.currency}`
                                            }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </template>
                </v-data-table>

            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination v-model="currentPage" :length="totalPages" class="my-4" density="compact"
                              @update:model-value="fetchInvoices"></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <v-dialog v-if="emailDialog" v-model="emailDialog" max-width="350px" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <div class="text-medium-emphasis ps-2">Email Invoice #: NEXTERA-{{ selectedInvoice.id }}</div>
                <v-btn :disabled="emailing" icon="mdi-close" size="small" variant="text" @click="emailDialog = false"/>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col class="pa-0" cols="12">
                        <v-text-field v-model="recipient" :rules="[vRules.required,vRules.email]"
                                      density="compact" label="Recipient Email" type="email" autofocus/>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-btn :disabled="!recipient"
                       :loading="emailing"
                       color="primary" size="small"
                       @click="emailInvoice">Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="invoiceDetailsDialog" max-width="500px" scrollable>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <p>Invoice details: NEXTERA-{{ selectedInvoiceItem.invoice_id }} ({{
                        selectedInvoiceItem.plan_name
                    }})</p>
                <v-btn icon="mdi-close" size="small" variant="text" @click="invoiceDetailsDialog = false"/>
            </v-card-title>
            <v-card-text>
                <v-table density="compact">
                    <thead>
                    <tr>
                        <th class="font-weight-bold">Asset</th>
                        <th class="font-weight-bold">Sl. no.</th>
                        <th class="font-weight-bold">Sku</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="loading">
                        <td colspan="3">
                            <v-skeleton-loader type="table-row@7"></v-skeleton-loader>
                        </td>
                    </tr>
                    <tr v-for="deviceDetail in selectedInvoiceItem.details" v-else>
                        <td>{{ deviceDetail.device.asset_label }}</td>
                        <td>
                            <router-link :to="`devices/${deviceDetail.device.slno}`" target="_blank">{{
                                    deviceDetail.device.slno
                                }}
                            </router-link>
                        </td>
                        <td>{{ deviceDetail.device.sku }}</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-card-text>
        </v-card>
    </v-dialog>

    <Create v-if="dialogSwitch"
            v-model="dialogSwitch"
            :companies="companies"
            @invoiceCreated="fetchInvoices"/>

</template>

<script setup>
import {computed, onMounted, reactive, ref} from 'vue';
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Create from "@/components/dialogs/invoices/Create.vue"
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const expanded = ref([]);
const tableHeaders = computed(() => {
    const headers = [
        {title: 'ID', align: 'start', sortable: false, key: 'id'},
        {title: 'Company', align: 'start', sortable: false, key: 'company_name'},
        {title: 'Total Price', align: 'start', sortable: false, key: 'total_price'},
        {title: 'Card Brand', align: 'start', sortable: false, key: 'brand'},
        {title: 'Card last 4', align: 'start', sortable: false, key: 'last_four'},
        {title: 'Invoice Date', align: 'start', sortable: false, key: 'invoice_date'},
        {title: 'Payment Date', align: 'start', sortable: false, key: 'payment_datetime'},
    ];
    if (user.type === 'admin') {
        headers.push({title: 'Source', align: 'start', sortable: false, key: 'client_source'});
        headers.push({title: 'IP Address', align: 'start', sortable: false, key: 'ip_address'});
    }
    headers.push({title: 'Status', align: 'start', sortable: false, key: 'status'});
    headers.push({title: 'Action', align: 'start', sortable: false, key: 'action'});
    return headers;
});
const tableItems = computed(() => {
    if (invoices.value.length) {
        return invoices.value.map((item) => {
            return {
                id: item.id,
                currency: item.currency,
                total_price: `${item.total_price} ${item.currency}`,
                company_name: item.company_name,
                brand: item.brand?.toUpperCase(),
                last_four: `${item.last_four ? '**** ' + item.last_four : ''}`,
                invoice_date: item.invoice_date,
                payment_datetime: item.payment_datetime,
                client_source: item.client_source,
                ip_address: item.ip_address,
                status: item.status,
                items: item.items,
                notes_count: item.notes_count ?? 0
            }
        })
    } else {
        return []
    }
})

const invoices = ref([]);
const companies = ref([]);
const totalPages = ref(1);
const currentPage = ref(1);
const paying = ref(false);
const markingPaid = ref(null);
const recalculating = ref(false);
const loading = ref(false);
const emailing = ref(false);
const recipient = ref(null);
const emailDialog = ref(false);
const downloading = ref(false);
const dialogSwitch = ref(false);
const selectedInvoice = ref(null);
const selectedInvoiceItem = ref(null);
const invoiceDetailsDialog = ref(false);
const {showSnackbar} = useSnackbarStore();
const listFormData = reactive({
    year: null,
    month: null,
    status: null,
    company: null,
    valid: false,
});
const statuses = [{value: 0, title: 'Pending'}, {value: 1, title: 'Paid'}, {value: 2, title: 'Cancelled'}];
const months = [
    {value: 1, title: 'January'},
    {value: 2, title: 'February'},
    {value: 3, title: 'March'},
    {value: 4, title: 'April'},
    {value: 5, title: 'May'},
    {value: 6, title: 'June'},
    {value: 7, title: 'July'},
    {value: 8, title: 'August'},
    {value: 9, title: 'September'},
    {value: 10, title: 'October'},
    {value: 11, title: 'November'},
    {value: 12, title: 'December'},
];
const years = [];
for (let i = 2024; i <= (new Date()).getFullYear(); i++) {
    years.push(i.toString());
}

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    }
    fetchInvoices();
});

const status = (id) => {
    return statuses.find((status) => status.value === id)['title'];
}
const showInvoiceItemDetail = (invoice, index) => {
    selectedInvoiceItem.value = invoice['items'][index];
    if (!selectedInvoiceItem.value.details) {
        fetchInvoiceDetails(invoice, index)
    }
    invoiceDetailsDialog.value = true;
}
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((errorResponse) => {
            if (errorResponse.response.data.message) {
                showSnackbar(errorResponse.response.data.message, 'error');
            } else {
                showSnackbar('Something went wrong', 'error');
            }
        })
}
const fetchInvoices = () => {
    loading.value = true;
    send('get', `${user.type === 'admin' ? 'admin/invoices' : `invoices`}`, {
        page: currentPage.value,
        ...listFormData
    })
        .then((response) => {
            loading.value = false;
            invoices.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((errorResponse) => {
            loading.value = false;
            if (errorResponse.response.data.message) {
                showSnackbar(errorResponse.response.data.message, 'error');
            } else {
                showSnackbar('Something went wrong', 'error');
            }
        });
}
const fetchInvoiceDetails = (invoice, index) => {
    loading.value = true;
    send('get', `${user.type === 'admin' ? `admin/invoices/item/${selectedInvoiceItem.value.id}` : `invoices/item/${selectedInvoiceItem.value.id}`}`)
        .then((response) => {
            if (response.data.data.details) {
                invoices.value[invoices.value.findIndex(row => row.id === invoice.id)]['items'][index]['details'] = response.data.data.details;
            }
        })
        .catch((errorResponse) => {
            console.log(errorResponse)
            showSnackbar("Invoice details couldn't fetched", 'error');
        }).finally(() => loading.value = false);
}
const payInvoice = (invoiceID) => {
    if (confirm("are you sure to pay invoice with your default payment method?")) {
        paying.value = true;
        send('post', `invoices/pay/${invoiceID}`)
            .then((response) => {
                if (response.data.success) {
                    invoices.value.map((invoice, index) => {
                        if (invoice.id === invoiceID) {
                            invoices.value[index]['status'] = response.data.data.status;
                            invoices.value[index]['brand'] = response.data.data.brand;
                            invoices.value[index]['last_four'] = response.data.data.last_four;
                            invoices.value[index]['payment_datetime'] = response.data.data.payment_datetime;
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((errorResponse) => {
                if (errorResponse.response.data.message) {
                    showSnackbar(errorResponse.response.data.message, 'error');
                } else {
                    showSnackbar('Something went wrong', 'error');
                }
            }).finally(() => paying.value = false);
    }
}
const markInvoicePaid = (invoiceId) => {
    if (!confirm('Mark this invoice as paid (offline payment)?')) return;
    markingPaid.value = invoiceId;
    send('post', `admin/invoices/${invoiceId}/mark-paid`)
        .then((response) => {
            if (response.data.success) {
                const idx = invoices.value.findIndex(i => i.id === invoiceId);
                if (idx !== -1) {
                    invoices.value[idx].status = 1;
                    invoices.value[idx].payment_datetime = response.data.data.payment_datetime;
                }
                showSnackbar(response.data.message);
            }
        })
        .catch((err) => {
            showSnackbar(err?.response?.data?.message || 'Something went wrong', 'error');
        })
        .finally(() => markingPaid.value = null);
}
const recalculateSubscriptions = () => {
    if (!confirm('Recalculate subscription end dates for all devices from paid invoice history?')) return;
    recalculating.value = true;
    send('post', 'admin/tools/recalculate-subscriptions')
        .then((response) => {
            if (response.data.success) {
                const updated = response.data.data?.updated ?? 0;
                showSnackbar(`Recalculation complete: ${updated} device(s) updated`);
            }
        })
        .catch((err) => {
            showSnackbar(err?.response?.data?.message || 'Recalculation failed', 'error');
        })
        .finally(() => recalculating.value = false);
}
const downloadInvoice = (invoiceID) => {
    downloading.value = true;
    send('get', `invoices/download/${invoiceID}`)
        .then((response) => {
            handleDownloadResponse(response, `NEXTERA-${invoiceID}`);
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Invoices could not be downloaded', 'error')
        })
        .finally(() => {
            downloading.value = false;
        });
}
const showEmailDialog = (invoice) => {
    selectedInvoice.value = invoice;
    const invoiceItem = invoices.value.find(item => {
        return item.id === selectedInvoice.value.id;
    });
    recipient.value = invoiceItem.email;
    emailDialog.value = true;
};
const emailInvoice = () => {
    emailing.value = true;
    if (!recipient.value) {
        showSnackbar('Recipient email address is required', 'error')
        return;
    }
    send('post', `invoices/email/${selectedInvoice.value.id}`, {recipient: recipient.value})
        .then((response) => {
            if (response.data.success) {
                emailDialog.value = false;
                selectedInvoice.value = null;
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Invoice could not be emailed', 'error')
        }).finally(() => {
        emailing.value = false;
    });
}
const handleDownloadResponse = (response, fileName) => {
    if (!response.data) {
        console.error("Server returned no data.");
        return;
    }
    let url = null;
    let byteCharacters = atob(response.data);
    let byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    let byteArray = new Uint8Array(byteNumbers);
    url = window.URL.createObjectURL(new Blob([byteArray], {type: "application/pdf"}));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // Set the file name, ensuring it has a .gz extension
    document.body.appendChild(link);
    link.click();
    link.remove(); // Clean up
    window.URL.revokeObjectURL(url); // Free up memory
}
</script>
<style>
td.v-data-table__td.v-data-table-column--align-start {
    text-align: left;
}
</style>
